import React, { useState, useEffect, useRef } from 'react';
import { Button, Typography, Checkbox, Spin } from 'antd';
import { ReactComponent as MinusIcon } from 'svg/graphicEditor/minus-big-black.svg';
import { ReactComponent as PlusIcon } from 'svg/graphicEditor/plus-big-black.svg';
import { fetchAndApplyAutocrop, applyAutocropToNode } from '../../../services/autocropService';
import globalStyles from '../../GlobalGraphicEditor.module.css';

const { Text } = Typography;

export default function CroppingControl({
  image,
  background,
  scaleImage,
  onPropertyChanged,
}) {
  const [isControlOpen, setControlOpen] = useState(false);
  const [cropBackground, setCropBackground] = useState(false);
  const [useFirstPixel, setUseFirstPixel] = useState(false);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!image) {
      setCropBackground(false);
      setUseFirstPixel(false);
      return;
    }
    setCropBackground(image.getAttr('cropBackground') || false);
    setUseFirstPixel(image.getAttr('useFirstPixel') || false);
  }, [image]);

  if (!image) {
    return null;
  }

  const finalizeUpdate = () => {
    scaleImage(image);
    onPropertyChanged();
  };

  const handleCropBackgroundChange = async (checked) => {
    setCropBackground(checked);
    image.setAttr('cropBackground', checked);
    const currentId = ++requestIdRef.current;
    setLoading(true);
    try {
      if (checked) {
        const url = image.getAttr('url');
        if (!url) return;
        await fetchAndApplyAutocrop(image, background, url, useFirstPixel);
        if (currentId !== requestIdRef.current) return;
      } else {
        await applyAutocropToNode(image, background, {
          cropped: false,
          bbox: null,
        });
        if (currentId !== requestIdRef.current) return;
      }
      finalizeUpdate();
    } catch (e) {
      console.error('Autocrop failed:', e);
    } finally {
      if (currentId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const handleFirstPixelChange = async (checked) => {
    if (!cropBackground) return;

    setUseFirstPixel(checked);
    image.setAttr('useFirstPixel', checked);
    const currentId = ++requestIdRef.current;
    setLoading(true);
    try {
      const url = image.getAttr('url');
      if (!url) return;
      await fetchAndApplyAutocrop(image, background, url, checked);
      if (currentId !== requestIdRef.current) return;
      finalizeUpdate();
    } catch (e) {
      console.error('Autocrop failed:', e);
    } finally {
      if (currentId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  return (
    <div>
      <div className={globalStyles.spaceBetweenBlock}>
        <Text className={globalStyles.smallTitle}>Autocropping</Text>
        <Button
          shape='circle'
          type='text'
          icon={isControlOpen ? <MinusIcon /> : <PlusIcon />}
          onClick={() => setControlOpen(!isControlOpen)}
        />
      </div>
      {isControlOpen && (
        <Spin spinning={loading}>
          <div
            className={`${globalStyles.innerBlock} ${globalStyles.selectBlock}`}
          >
            <Checkbox
              checked={cropBackground}
              onChange={(e) => handleCropBackgroundChange(e.target.checked)}
              disabled={loading}
              style={{ fontSize: '12px' }}
            >
              Remove excessive <b>whitish</b> background around object
            </Checkbox>
            {cropBackground && (
              <Checkbox
                checked={useFirstPixel}
                onChange={(e) => handleFirstPixelChange(e.target.checked)}
                disabled={loading}
                style={{ marginLeft: '20px', fontSize: '10px' }}
              >
                Use first pixel color (for non-whitish background)
              </Checkbox>
            )}
          </div>
        </Spin>
      )}
    </div>
  );
}
